import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const baseurl =
    process.env.REACT_APP_BASE_URL ||
    "http://localhost:8000/api";

const handleError = (error) => {
    return error?.response?.data || { detail: error?.message || "Request failed." };
};

const getHeaders = (token) => ({
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export const listCourses = createAsyncThunk(
    "course/listCourses",
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.get(`${baseurl}/courses/`, {
                headers: getHeaders(token),
            });
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);

export const listMyCourses = createAsyncThunk(
    "course/listMyCourses",
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.get(`${baseurl}/courses/my-courses/`, {
                headers: getHeaders(token),
            });
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);

export const getCourseDetails = createAsyncThunk(
    "course/getCourseDetails",
    async ({ slug }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.get(`${baseurl}/courses/${slug}/`, {
                headers: getHeaders(token),
            });
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);

export const getChapterPlayback = createAsyncThunk(
    "course/getChapterPlayback",
    async ({ chapterId }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.get(
                `${baseurl}/courses/chapters/${chapterId}/playback/`,
                { headers: getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            const apiError = handleError(error);
            return rejectWithValue(apiError);
        }
    }
);

export const markChapterProgress = createAsyncThunk(
    "course/markChapterProgress",
    async ({ chapterId, completed }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(
                `${baseurl}/courses/chapters/${chapterId}/progress/`,
                { completed },
                { headers: getHeaders(token) }
            );
                        return {
                ...response.data,
                chapterId: response.data?.chapterId ?? response.data?.chapter_id ?? response.data?.id ?? chapterId,
                completed: response.data?.completed ?? response.data?.is_completed ?? completed,
            };
        } catch (error) {
            const apiError = handleError(error);
            return rejectWithValue(apiError);
        }
    }
);

export const getCourseChatHistory = createAsyncThunk(
    "course/getCourseChatHistory",
    async ({ courseId }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const url = `${baseurl}/courses/${courseId}/chat/`;
                        const response = await axios.get(url, {
                headers: getHeaders(token),
            });
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);

export const sendMessageToAiTutor = createAsyncThunk(
    "course/sendMessageToAiTutor",
    async ({ courseId, message }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const url = `${baseurl}/courses/${courseId}/chat/`;
                        const response = await axios.post(
                url,
                { message },
                { headers: getHeaders(token) }
            );
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);


export const checkModuleAssessmentAnswer = createAsyncThunk(
    "course/checkModuleAssessmentAnswer",
    async ({ domainId, questionId, selectedOption, currentIndex }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(
                `${baseurl}/courses/domains/${domainId}/assessment/check/`,
                {
                    question_id: questionId,
                    selected_option: selectedOption,
                    current_index: currentIndex,
                },
                { headers: getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const submitModuleAssessment = createAsyncThunk(
    "course/submitModuleAssessment",
    async ({ domainId, answers }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(
                `${baseurl}/courses/domains/${domainId}/assessment/submit/`,
                { answers },
                { headers: getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const getQuizDrafts = createAsyncThunk(
    "course/getQuizDrafts",
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.get(`${baseurl}/courses/quiz-drafts/`, {
                headers: getHeaders(token),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const clearQuizDraft = createAsyncThunk(
    "course/clearQuizDraft",
    async ({ quizType, targetId }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(
                `${baseurl}/courses/quiz-drafts/clear/`,
                { quiz_type: quizType, target_id: targetId },
                { headers: getHeaders(token) }
            );
            return { ...response.data, quizType, targetId };
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const checkQuickQuizAnswer = createAsyncThunk(
    "course/checkQuickQuizAnswer",
    async ({ chapterId, questionId, selectedOption, currentIndex }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(
                `${baseurl}/courses/chapters/${chapterId}/quick-quiz/check/`,
                {
                    question_id: questionId,
                    selected_option: selectedOption,
                    current_index: currentIndex,
                },
                { headers: getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const submitQuickQuiz = createAsyncThunk(
    "course/submitQuickQuiz",
    async ({ chapterId, answers }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("access_token");
            const url = `${baseurl}/courses/chapters/${chapterId}/quick-quiz/submit/`;
            const response = await axios.post(
                url,
                { answers },
                { headers: getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            const apiError = handleError(error);
            return rejectWithValue(apiError);
        }
    }
);

